// The Sections entry on the Home Page Style bar: every section on the page as a thumbnail + name,
// with the same list affordances the Nav Pods panel established — reorder, add, delete, and click a
// row to open it.
//
// The canvas already carries a per-section edit button, but it is a 32px icon pinned to the corner
// of a merchant-colored band — findable once you know it is there, and easy to lose against a busy
// header. This panel is the page-level way in: one list, in page order, where a section is picked by
// recognizing its LOOK rather than by hunting the canvas for its controls.
//
// Row anatomy is deliberately the Nav Pods row (NavPods/StorefrontNavPodsEditor NavPodRow): preview,
// name over a summary line, stacked move chevrons, delete, and a centered icon-led add beneath the
// list. The one difference is what clicking the row does — a nav pod expands in place because its
// whole editor is that panel, while a section's editor is its own Panel, so the row opens that.
//
// The panel stays dumb (same contract as the other style panels): it owns no state, parses no config
// and writes nothing. It renders the sections it is handed and reports what the merchant asked for;
// StorefrontSectionsEditor owns the list and performs every mutation.

import React from 'react';
import { ActionButton, DefaultButton, Icon, IconButton, IContextualMenuItem, Panel, PanelType, Stack, Text, useTheme } from '@fluentui/react';
import { IStorefrontSection, IStorefrontStyle } from '../../../../models/Storefront';
import { resolveStyleProps } from '../stylesConfig';
import { useStorefrontStyles } from '../StorefrontStylesContext';
import { widthPctFor } from './sectionWidths';
import { sectionTypeLabel, sectionWidthLabel } from './sectionTypes';
import { isDarkColor } from '../colorUtils';
import './../StorefrontEditor.css';

const THUMB_WIDTH = 132;
const THUMB_HEIGHT = 74;
const BAND_HEIGHT = 17;

/**
 * The properties that carry a section's visual IDENTITY, as opposed to its box metrics.
 *
 * A thumbnail is 132px wide; a band whose real padding is `18px 32px` and whose real font is 34px
 * would render as a solid slab at that size and tell the merchant nothing. Colour, border, corner
 * and typeface survive scaling and are what makes a section recognisable, so the thumbnail takes
 * those and supplies its own metrics.
 */
const IDENTITY_PROPS = [
    'background', 'backgroundColor', 'backgroundImage', 'backgroundSize', 'backgroundPosition',
    'color', 'border', 'borderTop', 'borderBottom', 'borderLeft', 'borderRight',
    'borderColor', 'borderStyle', 'borderWidth', 'borderRadius', 'boxShadow',
    'fontFamily', 'fontWeight', 'fontStyle', 'textTransform', 'letterSpacing', 'opacity',
];

const identityStyle = (dict?: Record<string, any>): React.CSSProperties => {
    const out: Record<string, any> = {};
    if (!dict) return out;
    for (const key of IDENTITY_PROPS) {
        const value = dict[key];
        if (value !== undefined && value !== null && value !== '') out[key] = value;
    }
    return out;
};

/** Same colour, written the same way? Enough to catch the case below — both values come from the
 *  same config blob, so a merchant who set band and text to one colour set the same string twice. */
const sameColor = (a?: unknown, b?: unknown): boolean =>
    typeof a === 'string' && typeof b === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase();

interface ISectionThumbnailProps {
    section: IStorefrontSection;
    styles: IStorefrontStyle[];
    isDark?: boolean;
}

/**
 * A scaled-down impression of one section: its header band, its background, and a suggestion of
 * whatever it puts below the band (product cards, text lines, a media block, a rule).
 *
 * Not a second renderer — the canvas above is the faithful preview. This has to survive being 132px
 * wide, which is what the identity/metrics split above is for.
 */
const SectionThumbnail: React.FC<ISectionThumbnailProps> = ({ section, styles, isDark }) => {
    const styleProps = resolveStyleProps<Record<string, any>>(section.styleProps as any, 'styleProps', styles);
    const titleStyleProps = resolveStyleProps<Record<string, any>>(section.titleStyleProps as any, 'titleStyleProps', styles);
    const headerStyleProps = resolveStyleProps<Record<string, any>>(section.headerStyleProps as any, 'headerStyleProps', styles);
    const productStyleProps = resolveStyleProps<Record<string, any>>(section.productStyleProps as any, 'productStyleProps', styles);

    const bodyIdentity = { ...identityStyle(headerStyleProps), ...identityStyle(styleProps) };
    const bandIdentity = identityStyle(titleStyleProps);
    const cardIdentity = identityStyle(productStyleProps);

    const bodyBackground = bodyIdentity.backgroundColor ?? bodyIdentity.background;
    const bandBackground = bandIdentity.backgroundColor ?? bandIdentity.background ?? bodyBackground;
    // The band's own text colour is used when it is legible on the band. A merchant may set it TO the
    // band colour and let a knocked-out title block (homePageConfig.sectionTitleText, a page-level
    // rule this thumbnail has no access to) supply the contrast — faithful there means an unreadable
    // thumbnail, and a picker that cannot be read is not a picker. Fall back to band contrast.
    const bandContrast = bandBackground == null
        ? undefined
        : (isDarkColor(bandBackground, isDark) ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.75)');
    const bandColor = bandIdentity.color && !sameColor(bandIdentity.color, bandBackground)
        ? bandIdentity.color
        : (bandContrast ?? (isDark ? '#fff' : '#323130'));

    // Neutral stand-ins, tinted for the ground they sit on, used wherever the section's own config
    // says nothing about the block below the band.
    const inkOn = (dark?: boolean) => (dark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.22)');
    const bodyIsDark = isDarkColor(bodyBackground, isDark);
    const ink = inkOn(bodyIsDark);
    const cardStyle: React.CSSProperties = {
        borderRadius: 2,
        background: ink,
        ...cardIdentity,
        // Metrics are the thumbnail's, always — see IDENTITY_PROPS.
        height: 28,
        flex: 1,
        minWidth: 0,
    };
    const bar = (widthPct: number, key: React.Key): React.ReactElement =>
        <div key={key} style={{ height: 4, borderRadius: 2, background: ink, width: `${widthPct}%` }} />;

    const body = ((): React.ReactNode => {
        switch (section.type) {
            case 'divider':
                return <div style={{ height: 2, width: '100%', borderRadius: 1, background: ink, ...identityStyle(styleProps) }} />;
            case 'banner':
            case 'video':
                return <div style={{ height: 32, width: '100%', borderRadius: 2, background: ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {section.type === 'video' && <Icon iconName='Play' style={{ fontSize: 12, color: bodyIsDark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.5)' }} />}
                </div>;
            case 'richText':
                return <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                    {[100, 92, 64].map((w, i) => bar(w, i))}
                </div>;
            case 'singleProduct':
                return <div style={{ display: 'flex', gap: 5, width: '100%', alignItems: 'center' }}>
                    <div style={{ ...cardStyle, flex: '0 0 30px' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        {[100, 70].map((w, i) => bar(w, i))}
                    </div>
                </div>;
            default:
                // Every product-backed type reads as a row of cards, which is what they all render.
                return <div style={{ display: 'flex', gap: 4, width: '100%' }}>
                    {[0, 1, 2, 3].map((i) => <div key={i} style={cardStyle} />)}
                </div>;
        }
    })();

    return (
        <div
            className='storefront-section-thumb'
            style={{
                ...bodyIdentity,
                // The thumbnail's own frame wins over the section's: a 24px radius or a 6px border
                // scaled onto a 132px box reads as a different shape than the merchant configured.
                borderRadius: 3,
                width: THUMB_WIDTH,
                height: THUMB_HEIGHT,
                ...(bodyBackground ? {} : { background: isDark ? '#201f1e' : '#fff' }),
            }}
        >
            {/* hideTitle drops the band on the storefront, so it drops here too — that IS the
                difference the merchant is looking at when they scan this list. */}
            {!section.hideTitle && (
                <div
                    style={{
                        ...bandIdentity,
                        color: bandColor,
                        borderRadius: 0,
                        height: BAND_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 5px',
                        fontSize: 7,
                        lineHeight: 1,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {section.title || sectionTypeLabel(section.type)}
                </div>
            )}
            {/* The content column is inset to the section's own width fraction, so a half- or
                quarter-width section is recognisable as one at a glance. */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 6px', minHeight: 0 }}>
                <div style={{ width: `${widthPctFor(section.width)}%`, display: 'flex', justifyContent: 'center' }}>{body}</div>
            </div>
        </div>
    );
};

interface IStorefrontSectionsListPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    /** The sections to list, already filtered to the page being edited and in page order. */
    sections: IStorefrontSection[];
    /** A row was clicked — open that section's editor. */
    onSelect: (sectionId: string) => void;
    /** Reorder within the page. Absent at the ends, which is what disables the button. */
    onMove: (sectionId: string, dir: -1 | 1) => void;
    onDelete: (sectionId: string) => void;
    /**
     * The section types offered by Add Section, already built by the owner — the canvas button and
     * this one add the same things in the same order, and a type that existed in one place but not
     * the other would be a trap.
     */
    addMenuItems: IContextualMenuItem[];
    isDark?: boolean;
}

/** One row: preview, name over its summary line, reorder, delete. Mirrors NavPodRow's collapsed line. */
const SectionRow: React.FC<{
    section: IStorefrontSection;
    styles: IStorefrontStyle[];
    isDark?: boolean;
    onSelect: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    onDelete: () => void;
}> = ({ section, styles, isDark, onSelect, onMoveUp, onMoveDown, onDelete }) => {
    const typeLabel = sectionTypeLabel(section.type);
    const title = section.title?.trim() || typeLabel;
    const notes = [
        // Don't print the same word twice. That happens two ways: an untitled section already shows
        // its type AS the title, and a merchant may have named a section exactly after its type
        // ("Recommended For You"), which is why this compares the rendered title rather than
        // checking whether a title was set.
        ...(title.toLowerCase() === typeLabel.toLowerCase() ? [] : [typeLabel]),
        ...(section.width && section.width !== 'full' ? [`${sectionWidthLabel(section.width)} width`] : []),
        ...(section.hideTitle ? ['Header hidden'] : []),
    ];

    return (
        // A div, not a <button>: the row carries buttons of its own, which cannot nest inside one.
        // The keyboard handler and role put back what the <button> was giving us for free — nav pods
        // leave their row mouse-only, and there was no reason to copy that part.
        <div
            className='storefront-section-picker-row'
            role='button'
            tabIndex={0}
            title={`Open ${title}`}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
            }}
        >
            <SectionThumbnail section={section} styles={styles} isDark={isDark} />
            <span className='storefront-section-picker-text'>
                <span className='storefront-section-picker-title'>{title}</span>
                {/* Omitted entirely when empty — an empty span still takes the column's row gap,
                    which shifts the title off center. */}
                {notes.length > 0 && <span className='storefront-section-picker-notes'>{notes.join(' · ')}</span>}
            </span>
            {/* Every button stops propagation: they act ON the row, so they must not also open it. */}
            <Stack>
                <IconButton
                    iconProps={{ iconName: 'ChevronUpSmall' }}
                    ariaLabel='Move section up'
                    disabled={!onMoveUp}
                    onClick={(e) => { e.stopPropagation(); onMoveUp?.(); }}
                    styles={{ root: { height: 16 } }}
                />
                <IconButton
                    iconProps={{ iconName: 'ChevronDownSmall' }}
                    ariaLabel='Move section down'
                    disabled={!onMoveDown}
                    onClick={(e) => { e.stopPropagation(); onMoveDown?.(); }}
                    styles={{ root: { height: 16 } }}
                />
            </Stack>
            <IconButton
                iconProps={{ iconName: 'Delete' }}
                ariaLabel={`Delete ${title}`}
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
            />
            <Icon iconName='ChevronRight' className='storefront-section-picker-chevron' />
        </div>
    );
};

const StorefrontSectionsListPanel: React.FC<IStorefrontSectionsListPanelProps> = (props) => {
    const theme = useTheme();
    const { styles } = useStorefrontStyles();

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Sections'
            // Wider than smallFixedFar, which the row outgrew once it gained reorder and delete
            // buttons alongside the thumbnail. Narrower than the Nav Pods panel's 560px: that one
            // has to hold a whole pod editor when a row expands, this one only ever holds the list.
            type={PanelType.custom}
            customWidth='440px'
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            <Stack tokens={{ childrenGap: '0.5em' }} style={{ marginTop: 8 }}>
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    Every section on this page, in the order shoppers see them. Click one to open its settings.
                </Text>
                {props.sections.length === 0 && (
                    <Text styles={{ root: { color: theme.palette.neutralSecondary } }}>
                        No sections on this page yet. Add one to get started.
                    </Text>
                )}
                {props.sections.map((section, index) => (
                    <SectionRow
                        key={section.id ?? index}
                        section={section}
                        styles={styles}
                        isDark={props.isDark}
                        onSelect={() => props.onSelect(section.id ?? '')}
                        onMoveUp={index > 0 ? () => props.onMove(section.id ?? '', -1) : undefined}
                        onMoveDown={index < props.sections.length - 1 ? () => props.onMove(section.id ?? '', 1) : undefined}
                        onDelete={() => props.onDelete(section.id ?? '')}
                    />
                ))}
                {/* Centered icon-led action, exactly as the nav pods list ends. The menu is the one
                    difference: a section has a type, chosen when it is created. */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <ActionButton
                        iconProps={{ iconName: 'Add' }}
                        text='Add Section'
                        menuProps={{ items: props.addMenuItems }}
                        styles={{ root: { height: 28 }, label: { fontSize: 12 } }}
                    />
                </div>
                <DefaultButton text='Done' onClick={props.onDismiss} />
            </Stack>
        </Panel>
    );
};

export default StorefrontSectionsListPanel;
